import { useEffect, type ReactNode } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CartDrawer } from "../cart/CartDrawer";
// CHAT AI DISATTIVATA (non serve più) — conservata in ../chat/ChatWidget.tsx.
// Per riattivarla: de-commentare l'import + <ChatWidget /> qui sotto e
// rimuovere il ContactWidget.
// import { ChatWidget } from "../chat/ChatWidget";
import { ContactWidget } from "../chat/ContactWidget";
import { CookieConsent } from "../cookies/CookieConsent";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { saveLocale, type Locale, isValidLocale } from "./locale";
import { staticTitleForPath, BRAND } from "./pageTitle";
import { startTracker, track } from "../analytics/tracker";

/**
 * `page_view` verso il collector proprietario, una per navigazione.
 *
 * Il sito è una SPA: dopo il primo caricamento il browser non richiede più
 * pagine, quindi senza questo la navigazione interna risulterebbe invisibile.
 * L'invio avviene su cambio di percorso, come fa GTM con l'history change, così
 * i due sistemi restano confrontabili durante il doppio binario.
 */
function TrackPageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    startTracker();
    track("page_view", { referrer_host: undefined });
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

/** Il <title> di index.html: se il documento ha ancora questo, il server non
 *  ha iniettato il titolo della pagina. */
const TITOLO_DEL_GUSCIO = "Granborgo - Premium Gift Boxes";
/** Percorso del primo caricamento: lì il titolo lo ha già messo il server. */
let percorsoIniziale: string | null = typeof window === "undefined" ? null : window.location.pathname;

/** Titolo <title> dinamico per rotta+lingua. Per prodotto/blog è la pagina a
 *  impostarlo (usePageTitle) dopo il fetch: qui mettiamo un fallback pulito.
 *  Al primo caricamento resta quello del server, già nella lingua giusta e più
 *  ricco («Granborgo — Confezioni regalo aziendali dall'Italia»): prima veniva
 *  sostituito subito, ed era quello che Google leggeva dopo il rendering. */
function DocumentTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    if (pathname === percorsoIniziale && document.title && document.title !== TITOLO_DEL_GUSCIO) return;
    percorsoIniziale = null;
    document.title = staticTitleForPath(pathname, t) ?? BRAND;
  }, [pathname, t, i18n.language]);
  return null;
}

export function LocaleLayout({ children }: { children: ReactNode }) {
  const { locale } = useParams();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (locale && isValidLocale(locale)) {
      i18n.changeLanguage(locale);
      saveLocale(locale as Locale);
      document.documentElement.lang = locale;
    }
  }, [locale, i18n]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <ScrollToTop />
      <TrackPageViews />
      <DocumentTitle />
      <Header />
      <main style={{ flex: 1 }}>{children}</main>
      <Footer />
      <CartDrawer />
      {/* <ChatWidget />  ← chat AI disattivata, sostituita dal form di contatto */}
      {/* Il widget è su ogni pagina, fuori dal boundary delle rotte: un suo errore
          di render non deve mai rendere bianco il sito. */}
      <ErrorBoundary resetKey={pathname} fallback={null}>
        <ContactWidget />
      </ErrorBoundary>
      <CookieConsent />
    </div>
  );
}
